/**
 * Auth state + actions exposed via React context.
 *
 * Reads tokens from sessionStorage on mount, derives the user identity from
 * the id_token claims, and provides login / logout / refresh actions plus
 * a fresh-on-demand getAccessToken() that auto-refreshes when needed.
 *
 * Single source of truth for "am I logged in?" across the app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { beginLogin, logout as oidcLogout, refresh } from './auth-client.js';
import {
  decodeIdToken,
  getTokens,
  hasValidAccessToken,
  needsRefresh,
  type IdTokenClaims,
} from './token-store.js';

export interface AuthState {
  /** True once initial token rehydration is done. */
  ready: boolean;
  /** Logged-in user claims if any (sub, username, is_admin). */
  user: IdTokenClaims | null;
  /** Convenience: user && user.is_admin. */
  isAuthenticated: boolean;
  /** Kick off PKCE flow; redirects browser. */
  signIn: (returnTo?: string) => Promise<void>;
  /** Clear tokens + redirect to /login (and OIDC end_session if id_token present). */
  signOut: () => Promise<void>;
  /** Return a valid access_token, refreshing if needed. Throws if no refresh possible. */
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<IdTokenClaims | null>(null);

  // Rehydrate from sessionStorage on mount.
  useEffect(() => {
    const t = getTokens();
    if (t) {
      setUser(decodeIdToken(t.id_token));
    }
    setReady(true);
  }, []);

  const signIn = useCallback(async (returnTo = '/') => {
    await beginLogin(returnTo);
  }, []);

  const signOut = useCallback(async () => {
    // CRITICAL: do NOT setUser(null) before calling oidcLogout().
    //
    // Race condition (reproduced headlessly in R21.6):
    //   - setUser(null) triggers a React re-render
    //   - AuthGuard's useEffect sees `!isAuthenticated`
    //   - AuthGuard calls signIn() → window.location.assign(authorization_endpoint)
    //   - This races with oidcLogout's window.location.assign(end_session_url)
    //   - The /oidc/auth navigation wins (~4ms later), end_session gets cancelled
    //   - The _session cookie is never cleared because /oidc/end_session/confirm
    //     never gets POSTed
    //   - /oidc/auth on the still-alive _session silently re-auths
    //   - User bounces straight back to /gpu, "logout didn't work"
    //
    // oidcLogout() handles all the teardown (sessionStorage clear, server-side
    // session destroy via end_session) and then hard-navigates away. After the
    // post-logout redirect lands the user on /login, AuthProvider re-mounts on
    // a fresh page and reads (empty) sessionStorage cleanly. No React state
    // change needed here.
    await oidcLogout();
  }, []);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const t = getTokens();
    if (!t) throw new Error('not_authenticated');
    if (hasValidAccessToken()) return t.access_token;
    if (needsRefresh()) {
      const fresh = await refresh();
      const after = getTokens();
      if (after) setUser(decodeIdToken(after.id_token));
      return fresh;
    }
    return t.access_token;
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      isAuthenticated: !!user,
      signIn,
      signOut,
      getAccessToken,
    }),
    [ready, user, signIn, signOut, getAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside AuthProvider');
  return v;
}
